import React, { useEffect, useMemo, useRef, useState } from 'react';
import useGameBase from '../../hooks/useGameBase';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function formatDelta(value, suffix = '') {
  const numericValue = Number(value) || 0;
  const rounded = Math.round(numericValue);
  return `${rounded >= 0 ? '+' : ''}${rounded}${suffix}`;
}

function formatOrdinal(value) {
  const numericValue = Math.max(1, Math.floor(Number(value) || 1));
  const modulo100 = numericValue % 100;
  if (modulo100 >= 11 && modulo100 <= 13) {
    return `${numericValue}th`;
  }

  switch (numericValue % 10) {
    case 1:
      return `${numericValue}st`;
    case 2:
      return `${numericValue}nd`;
    case 3:
      return `${numericValue}rd`;
    default:
      return `${numericValue}th`;
  }
}

function calculateThrowAccuracy(indicatorPosition) {
  return clamp(1 - (Math.abs(indicatorPosition - 0.5) / 0.5), 0, 1);
}

function applyAccuracyToShot(shotSettings, indicatorPosition) {
  const accuracy = calculateThrowAccuracy(indicatorPosition);
  const drift = indicatorPosition - 0.5;
  const driftMagnitude = Math.abs(drift);
  const driftAfterDeadzone = Math.max(0, driftMagnitude - 0.09);
  const normalizedMiss = clamp(driftAfterDeadzone / 0.41, 0, 1);
  const driftPenalty = Math.pow(normalizedMiss, 1.85);
  const angleAdjustment = Math.sign(drift) * driftPenalty * 12;
  const curveAdjustment = 0.94 + ((1 - driftPenalty) * 0.06);
  return {
    angle: clamp(shotSettings.angle + angleAdjustment, -38, 38),
    power: shotSettings.power,
    spin: shotSettings.spin,
    curve: clamp(shotSettings.curve * curveAdjustment, 0, 1),
    accuracy,
  };
}

const PREVIEW_PHYSICS = {
  minLaunchSpeed: 4,
  maxLaunchSpeed: 22,
  friction: 0.992,
  stopSpeed: 0.11,
  curlForce: 0.021,
  stepDuration: 0.08,
  maxSteps: 420,
};

const POWER_CURVE_EXPONENT = 2.15;
const PREVIEW_VIEWBOX = {
  minX: 0,
  minY: -6,
  width: 100,
  height: 158,
};

function buildPath(points) {
  return (points || []).reduce((path, point, index) => (
    `${path}${index === 0 ? 'M' : ' L'} ${point.x} ${point.y}`
  ), '');
}

function isOutOfBounds(sheet, x, y) {
  const stoneRadius = sheet.stoneRadius || 2.8;
  return (
    x < (sheet.playMinX - stoneRadius)
    || x > (sheet.playMaxX + stoneRadius)
    || y < (sheet.playMinY - stoneRadius)
    || y > (sheet.playMaxY + stoneRadius)
  );
}

function getLaunchSpeed(power) {
  const normalizedPower = clamp(Number(power) || 0, 0.2, 1);
  const curvedPower = Math.pow(normalizedPower, POWER_CURVE_EXPONENT);
  return PREVIEW_PHYSICS.minLaunchSpeed + ((PREVIEW_PHYSICS.maxLaunchSpeed - PREVIEW_PHYSICS.minLaunchSpeed) * curvedPower);
}

function projectPreview(sheet, angle, power, spin, curve) {
  const shotAngleRadians = (Number(angle) || 0) * (Math.PI / 180);
  const launchSpeed = getLaunchSpeed(power);
  let x = sheet.hackX;
  let y = sheet.hackY;
  let vx = Math.sin(shotAngleRadians) * launchSpeed;
  let vy = -Math.cos(shotAngleRadians) * launchSpeed;
  const points = [{ x, y }];

  for (let step = 0; step < PREVIEW_PHYSICS.maxSteps; step += 1) {
    const speed = Math.sqrt((vx * vx) + (vy * vy));
    if (speed <= PREVIEW_PHYSICS.stopSpeed) {
      break;
    }

    const nx = vx / speed;
    const ny = vy / speed;
    const curlScale = 0.65 + (0.35 * clamp(speed / PREVIEW_PHYSICS.maxLaunchSpeed, 0, 1));
    const curlStrength = spin * curve * PREVIEW_PHYSICS.curlForce * curlScale;
    vx += (-ny * curlStrength);
    vy += (nx * curlStrength);
    vy = Math.min(vy, -Math.min(0.08, Math.max(0.025, speed * 0.04)));
    x += vx * PREVIEW_PHYSICS.stepDuration;
    y += vy * PREVIEW_PHYSICS.stepDuration;
    vx *= PREVIEW_PHYSICS.friction;
    vy *= PREVIEW_PHYSICS.friction;

    if (isOutOfBounds(sheet, x, y)) {
      break;
    }

    if ((step % 2) === 0) {
      points.push({ x, y });
    }
  }

  const settledPoint = points[points.length - 1] || { x: sheet.hackX, y: sheet.hackY };
  return {
    path: buildPath(points),
    startX: sheet.hackX,
    startY: sheet.hackY,
    endX: settledPoint.x,
    endY: settledPoint.y,
  };
}

function getPowerHandlePoint(sheet, angle, power) {
  const handleAngleRadians = (Number(angle) || 0) * (Math.PI / 180);
  const handleDistance = 18 + (clamp(Number(power) || 0, 0, 1) * 102);
  return {
    x: clamp(sheet.hackX + (Math.sin(handleAngleRadians) * handleDistance), PREVIEW_VIEWBOX.minX, PREVIEW_VIEWBOX.minX + PREVIEW_VIEWBOX.width),
    y: clamp(sheet.hackY - (Math.cos(handleAngleRadians) * handleDistance), PREVIEW_VIEWBOX.minY, PREVIEW_VIEWBOX.minY + PREVIEW_VIEWBOX.height),
  };
}

function getPowerPreviewColor(power, isInteractive) {
  if (!isInteractive) return 'rgba(100,116,139,0.45)';
  const normalizedPower = clamp(((Number(power) || 0) - 0.2) / 0.8, 0, 1);
  const hue = 145 - (145 * normalizedPower);
  return `hsla(${hue}, 84%, 46%, 0.92)`;
}

function buildEditableHandles(sheet, shotSettings, powerHandlePoint, curveDirection) {
  const start = { x: sheet.hackX, y: sheet.hackY };
  const end = powerHandlePoint;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.max(1, Math.sqrt((dx * dx) + (dy * dy)));
  const perpX = -(dy / length);
  const perpY = dx / length;
  const curveBaseX = start.x + (dx * 0.52);
  const curveBaseY = start.y + (dy * 0.52);
  const spinBaseX = start.x + (dx * 0.28);
  const spinBaseY = start.y + (dy * 0.28);

  return {
    power: end,
    curveBase: { x: curveBaseX, y: curveBaseY },
    curve: {
      x: curveBaseX + (perpX * curveDirection * (shotSettings.curve * 18)),
      y: curveBaseY + (perpY * curveDirection * (shotSettings.curve * 18)),
    },
    spinBase: { x: spinBaseX, y: spinBaseY },
    spin: {
      x: spinBaseX + (perpX * shotSettings.spin * 14),
      y: spinBaseY + (perpY * shotSettings.spin * 14),
    },
  };
}

function mapPointerToSheet(clientX, clientY, svgElement) {
  if (!svgElement) return null;

  const svgPoint = svgElement.createSVGPoint();
  const screenMatrix = svgElement.getScreenCTM();
  if (!screenMatrix) return null;

  svgPoint.x = clientX;
  svgPoint.y = clientY;

  const localPoint = svgPoint.matrixTransform(screenMatrix.inverse());
  return {
    x: clamp(localPoint.x, PREVIEW_VIEWBOX.minX, PREVIEW_VIEWBOX.minX + PREVIEW_VIEWBOX.width),
    y: clamp(localPoint.y, PREVIEW_VIEWBOX.minY, PREVIEW_VIEWBOX.minY + PREVIEW_VIEWBOX.height),
  };
}

function getNearestEditableHandle(point, editableHandles, radius = 8.5) {
  if (!point || !editableHandles) return null;

  const candidates = ['power', 'curve', 'spin']
    .map((handleType) => {
      const handle = editableHandles[handleType];
      if (!handle) return null;

      const dx = point.x - handle.x;
      const dy = point.y - handle.y;
      return {
        handleType,
        distance: Math.sqrt((dx * dx) + (dy * dy)),
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.distance - right.distance);

  if (candidates.length === 0 || candidates[0].distance > radius) {
    return null;
  }

  return candidates[0].handleType;
}


function getCurrentScoringState(stones, sheet) {
  const buttonX = Number(sheet?.buttonX) || 50;
  const buttonY = Number(sheet?.buttonY) || 28;
  const houseRadius = Number(sheet?.houseRadius) || 18;

  const stonesInHouse = (stones || [])
    .map((stone) => {
      const dx = (Number(stone.x) || 0) - buttonX;
      const dy = (Number(stone.y) || 0) - buttonY;
      return {
        ...stone,
        distanceToButton: Math.sqrt((dx * dx) + (dy * dy)),
      };
    })
    .filter((stone) => Number.isFinite(stone.distanceToButton) && stone.distanceToButton <= houseRadius)
    .sort((left, right) => left.distanceToButton - right.distanceToButton);

  if (stonesInHouse.length === 0) {
    return {
      leaderId: null,
      count: 0,
      closestDistance: null,
    };
  }

  const leaderId = stonesInHouse[0].playerId;
  let count = 0;
  for (const stone of stonesInHouse) {
    if (stone.playerId !== leaderId) break;
    count += 1;
  }

  return {
    leaderId,
    count,
    closestDistance: stonesInHouse[0].distanceToButton,
  };
}

function Stone({ stone, selected }) {
  return (
    <g>
      <circle
        cx={stone.x}
        cy={stone.y}
        r="2.8"
        fill={stone.color || '#38bdf8'}
        stroke={selected ? '#f8fafc' : 'rgba(15, 23, 42, 0.65)'}
        strokeWidth={selected ? '0.9' : '0.5'}
      />
      <circle cx={stone.x} cy={stone.y} r="1.05" fill="rgba(255,255,255,0.28)" />
      <text
        x={stone.x}
        y={stone.y + 0.7}
        textAnchor="middle"
        fontSize="1.8"
        fontWeight="700"
        fill="#f8fafc"
        style={{ userSelect: 'none' }}
      >
        {String(stone.username || stone.playerId || '?').slice(0, 1).toUpperCase()}
      </text>
    </g>
  );
}

function interpolateStoneFrame(fromFrame, toFrame, progress) {
  const nextStoneById = new Map((toFrame?.stones || []).map((stone) => [stone.id, stone]));
  return (fromFrame?.stones || []).map((stone) => {
    const nextStone = nextStoneById.get(stone.id) || stone;
    return {
      ...nextStone,
      x: stone.x + ((nextStone.x - stone.x) * progress),
      y: stone.y + ((nextStone.y - stone.y) * progress),
    };
  });
}

export default function CurlingGame({ gameState = {}, gamePlayers = [], user = {}, socket, roomId }) {
  const { submitAction, isLocalPlayerTurn, perspectiveIds } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const sheet = useMemo(() => (gameState.sheet || {
    playMinX: 8,
    playMaxX: 92,
    playMinY: -18,
    playMaxY: 150,
    buttonX: 50,
    buttonY: 28,
    houseRadius: 18,
    fourFootRadius: 8,
    stoneRadius: 2.8,
    hackX: 50,
    hackY: 144,
  }), [gameState.sheet]);
  const [shotSettings, setShotSettings] = useState({ angle: 0, power: 0.7, spin: 0, curve: 0 });
  const [curveDirection, setCurveDirection] = useState(1);
  const [aimHandlePoint, setAimHandlePoint] = useState(() => getPowerHandlePoint({
    playMinX: 8,
    playMaxX: 92,
    playMinY: -18,
    playMaxY: 150,
    buttonX: 50,
    buttonY: 28,
    houseRadius: 18,
    fourFootRadius: 8,
    stoneRadius: 2.8,
    hackX: 50,
    hackY: 144,
  }, 0, 0.7));
  const [animatedStones, setAnimatedStones] = useState(null);
  const [accuracyChallengeActive, setAccuracyChallengeActive] = useState(false);
  const [accuracyIndicatorPosition, setAccuracyIndicatorPosition] = useState(0.5);
  const [endPauseActive, setEndPauseActive] = useState(false);
  const aimPadRef = useRef(null);
  const previewSvgRef = useRef(null);
  const draggingRef = useRef(false);
  const replayAnimationRef = useRef(null);
  const replayResetTimeoutRef = useRef(null);
  const endPauseTimeoutRef = useRef(null);
  const lastPlayedReplayKeyRef = useRef(null);
  const accuracyAnimationRef = useRef(null);
  const accuracyPhaseRef = useRef(0);
  const activeHandleRef = useRef(null);
  const activeTouchIdRef = useRef(null);

  const players = useMemo(() => gameState.players || [], [gameState.players]);
  const matchScores = useMemo(() => gameState.matchScores || {}, [gameState.matchScores]);
  const isMyTurn = !gameState.finished && isLocalPlayerTurn();

  const rankedPlayers = useMemo(() => {
    return [...players].sort((left, right) => {
      const leftScore = matchScores[left.socketId] || 0;
      const rightScore = matchScores[right.socketId] || 0;
      if (rightScore !== leftScore) return rightScore - leftScore;
      return String(left.username || left.socketId).localeCompare(String(right.username || right.socketId));
    });
  }, [matchScores, players]);

  const scoringLeaderId = gameState.lastEndSummary?.scoringPlayerId || null;
  const preview = useMemo(() => projectPreview(sheet, shotSettings.angle, shotSettings.power, shotSettings.spin, shotSettings.curve), [sheet, shotSettings]);
  const editableHandles = useMemo(() => buildEditableHandles(sheet, shotSettings, aimHandlePoint, curveDirection), [aimHandlePoint, curveDirection, sheet, shotSettings]);
  const previewStroke = useMemo(() => getPowerPreviewColor(shotSettings.power, isMyTurn), [isMyTurn, shotSettings.power]);
  const accuracySpeed = useMemo(() => 0.038 + (Math.pow(clamp(shotSettings.power, 0, 1), 1.6) * 0.07), [shotSettings.power]);
  const replay = gameState.lastReplay || null;
  const localPlayerSocketId = useMemo(() => {
    const playerList = gameState.players || gamePlayers || [];
    for (const id of perspectiveIds || []) {
      const directPlayer = playerList.find((player) => player?.socketId === id);
      if (directPlayer?.socketId) return directPlayer.socketId;

      const matchedPlayer = playerList.find((player) => (
        player?.socketId === id
        || player?.userId === id
        || player?.id === id
        || player?.username === id
      ));
      if (matchedPlayer?.socketId) return matchedPlayer.socketId;
    }
    return null;
  }, [gamePlayers, gameState.players, perspectiveIds]);
  const nextShotNumber = useMemo(() => {
    if (!localPlayerSocketId) return 1;
    return Math.max(1, Number(gameState.shotsTakenByPlayer?.[localPlayerSocketId] || 0) + 1);
  }, [gameState.shotsTakenByPlayer, localPlayerSocketId]);
  const replayKey = useMemo(() => {
    if (!replay || !Array.isArray(replay.frames) || replay.frames.length === 0) return null;
    return `${gameState.lastShot?.timestamp || 0}:${replay.frames.length}`;
  }, [gameState.lastShot?.timestamp, replay]);
  const renderedStones = useMemo(() => animatedStones || gameState.stones || [], [animatedStones, gameState.stones]);
  const currentScoringState = useMemo(() => getCurrentScoringState(renderedStones, sheet), [renderedStones, sheet]);
  const endPauseSummary = useMemo(() => {
    if (!gameState.lastEndSummary || !gameState.lastShot) return null;
    if (gameState.lastEndSummary.endNumber !== gameState.lastShot.endNumber) return null;
    if (!gameState.finished && Number(gameState.endNumber) <= Number(gameState.lastEndSummary.endNumber)) return null;
    return gameState.lastEndSummary;
  }, [gameState.endNumber, gameState.finished, gameState.lastEndSummary, gameState.lastShot]);

  const updateHandleFromPointer = (handleType, point) => {
    if (!point) return;

    if (handleType === 'power') {
      setAimHandlePoint(point);
    }

    setShotSettings((current) => {
      const startX = sheet.hackX;
      const startY = sheet.hackY;
      const handlePoint = handleType === 'power' ? point : aimHandlePoint;
      const endDx = handlePoint.x - startX;
      const endDy = handlePoint.y - startY;
      const endLength = Math.max(1, Math.sqrt((endDx * endDx) + (endDy * endDy)));
      const perpX = -(endDy / endLength);
      const perpY = endDx / endLength;

      if (handleType === 'power') {
        const dx = point.x - startX;
        const dy = startY - point.y;
        const distance = Math.sqrt((dx * dx) + (dy * dy));
        return {
          ...current,
          angle: clamp((Math.atan2(dx, Math.max(8, dy)) * 180) / Math.PI, -38, 38),
          power: clamp((distance - 18) / 102, 0, 1),
        };
      }

      if (handleType === 'curve') {
        const offset = ((point.x - editableHandles.curveBase.x) * perpX) + ((point.y - editableHandles.curveBase.y) * perpY);
        if (offset !== 0) {
          setCurveDirection(Math.sign(offset));
        }
        return {
          ...current,
          curve: clamp(Math.abs(offset) / 18, 0, 1),
        };
      }

      if (handleType === 'spin') {
        const offset = ((point.x - editableHandles.spinBase.x) * perpX) + ((point.y - editableHandles.spinBase.y) * perpY);
        return {
          ...current,
          spin: clamp(offset / 14, -1, 1),
        };
      }

      return current;
    });
  };

  const stopHandleDrag = (pointerId = null) => {
    draggingRef.current = false;
    activeHandleRef.current = null;

    if (pointerId !== null && pointerId !== undefined) {
      aimPadRef.current?.releasePointerCapture?.(pointerId);
    }
  };

  const beginHandleDrag = (handleType, point, pointerId = null) => {
    if (!point) return;

    activeHandleRef.current = handleType;
    draggingRef.current = true;

    if (pointerId !== null && pointerId !== undefined) {
      aimPadRef.current?.setPointerCapture?.(pointerId);
    }

    updateHandleFromPointer(handleType, point);
  };

  const startHandleDrag = (handleType) => (event) => {
    if (!isMyTurn || accuracyChallengeActive || !!animatedStones) return;
    if (event.pointerType === 'touch') return;
    event.preventDefault();
    event.stopPropagation();
    beginHandleDrag(handleType, mapPointerToSheet(event.clientX, event.clientY, previewSvgRef.current), event.pointerId);
  };

  const handleAimPointerDown = (event) => {
    if (event.pointerType === 'touch') return;
    if (!isMyTurn || accuracyChallengeActive || !!animatedStones) {
      stopHandleDrag();
      return;
    }

    event.preventDefault();
    const point = mapPointerToSheet(event.clientX, event.clientY, previewSvgRef.current);
    const targetHandle = event.target?.dataset?.handle || getNearestEditableHandle(point, editableHandles);
    beginHandleDrag(targetHandle || 'power', point, event.pointerId);
  };

  const handleAimPointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    if (!draggingRef.current || !activeHandleRef.current) return;
    if (event?.cancelable) {
      event.preventDefault();
    }
    updateHandleFromPointer(activeHandleRef.current, mapPointerToSheet(event.clientX, event.clientY, previewSvgRef.current));
  };

  const handleAimPointerUp = (event) => {
    if (event.pointerType === 'touch') return;
    if (event?.cancelable) {
      event.preventDefault();
    }
    stopHandleDrag(event.pointerId);
  };

  const handleAimPointerLeave = (event) => {
    if (event.pointerType !== 'mouse') return;
    handleAimPointerUp(event);
  };

  const handleThrow = async (indicatorPosition = 0.5) => {
    if (!isMyTurn) return;
    const adjustedShot = applyAccuracyToShot(shotSettings, indicatorPosition);
    const response = await submitAction({
      type: 'throw',
      angle: adjustedShot.angle,
      power: adjustedShot.power,
      spin: adjustedShot.spin,
      curve: adjustedShot.curve,
    });
    if (response?.status && response.status !== 'ok' && response.status !== 'finished') {
      return;
    }
  };

  const startAccuracyChallenge = () => {
    if (!isMyTurn || !!gameState.finished || !!animatedStones || endPauseActive) return;
    setAccuracyChallengeActive(true);
  };

  const completeAccuracyChallenge = async () => {
    if (!accuracyChallengeActive) return;
    setAccuracyChallengeActive(false);
    await handleThrow(accuracyIndicatorPosition);
  };

  useEffect(() => {
    setAimHandlePoint((current) => {
      if (!current) {
        return getPowerHandlePoint(sheet, 0, 0.7);
      }

      return {
        x: clamp(current.x, PREVIEW_VIEWBOX.minX, PREVIEW_VIEWBOX.minX + PREVIEW_VIEWBOX.width),
        y: clamp(current.y, PREVIEW_VIEWBOX.minY, PREVIEW_VIEWBOX.minY + PREVIEW_VIEWBOX.height),
      };
    });
  }, [sheet]);

  useEffect(() => {
    if (!replayKey || replayKey === lastPlayedReplayKeyRef.current || !Array.isArray(replay?.frames) || replay.frames.length < 2) {
      return undefined;
    }

    const frames = replay.frames;
    const frameDurationMs = Math.max(16, Number(replay.frameDurationMs) || 32);
    const totalDurationMs = (frames.length - 1) * frameDurationMs;
    const startedAt = performance.now();
    lastPlayedReplayKeyRef.current = replayKey;

    if (replayResetTimeoutRef.current) {
      window.clearTimeout(replayResetTimeoutRef.current);
      replayResetTimeoutRef.current = null;
    }

    if (endPauseTimeoutRef.current) {
      window.clearTimeout(endPauseTimeoutRef.current);
      endPauseTimeoutRef.current = null;
    }

    setEndPauseActive(false);

    if (replayAnimationRef.current) {
      window.cancelAnimationFrame(replayAnimationRef.current);
      replayAnimationRef.current = null;
    }

    setAnimatedStones(frames[0].stones || []);

    const tick = (now) => {
      const elapsedMs = Math.max(0, now - startedAt);
      if (elapsedMs >= totalDurationMs) {
        setAnimatedStones(frames[frames.length - 1].stones || []);
        replayAnimationRef.current = null;
        if (endPauseSummary) {
          setEndPauseActive(true);
          endPauseTimeoutRef.current = window.setTimeout(() => {
            setEndPauseActive(false);
            setAnimatedStones(null);
            endPauseTimeoutRef.current = null;
          }, 1800);
        } else {
          replayResetTimeoutRef.current = window.setTimeout(() => {
            setAnimatedStones(null);
            replayResetTimeoutRef.current = null;
          }, frameDurationMs);
        }
        return;
      }

      const frameIndex = Math.min(frames.length - 2, Math.floor(elapsedMs / frameDurationMs));
      const frameProgress = (elapsedMs - (frameIndex * frameDurationMs)) / frameDurationMs;
      const nextStones = interpolateStoneFrame(frames[frameIndex], frames[frameIndex + 1], frameProgress);
      setAnimatedStones(nextStones);
      replayAnimationRef.current = window.requestAnimationFrame(tick);
    };

    replayAnimationRef.current = window.requestAnimationFrame(tick);

    return () => {
      if (replayAnimationRef.current) {
        window.cancelAnimationFrame(replayAnimationRef.current);
        replayAnimationRef.current = null;
      }
      if (replayResetTimeoutRef.current) {
        window.clearTimeout(replayResetTimeoutRef.current);
        replayResetTimeoutRef.current = null;
      }
      if (endPauseTimeoutRef.current) {
        window.clearTimeout(endPauseTimeoutRef.current);
        endPauseTimeoutRef.current = null;
      }
    };
  }, [endPauseSummary, replay, replayKey]);

  useEffect(() => {
    if (replayKey || animatedStones == null) return undefined;
    setAnimatedStones(null);
    return undefined;
  }, [animatedStones, replayKey]);

  useEffect(() => {
    if (!accuracyChallengeActive) {
      if (accuracyAnimationRef.current) {
        window.cancelAnimationFrame(accuracyAnimationRef.current);
        accuracyAnimationRef.current = null;
      }
      accuracyPhaseRef.current = 0;
      setAccuracyIndicatorPosition(0.5);
      return undefined;
    }

    const animate = () => {
      accuracyPhaseRef.current += accuracySpeed;
      setAccuracyIndicatorPosition((Math.sin(accuracyPhaseRef.current) + 1) / 2);
      accuracyAnimationRef.current = window.requestAnimationFrame(animate);
    };

    accuracyAnimationRef.current = window.requestAnimationFrame(animate);

    return () => {
      if (accuracyAnimationRef.current) {
        window.cancelAnimationFrame(accuracyAnimationRef.current);
        accuracyAnimationRef.current = null;
      }
    };
  }, [accuracyChallengeActive, accuracySpeed]);

  useEffect(() => {
    if (isMyTurn && !animatedStones && !gameState.finished) return;
    setAccuracyChallengeActive(false);
  }, [animatedStones, endPauseActive, gameState.finished, isMyTurn]);

  useEffect(() => {
    const aimPad = aimPadRef.current;
    if (!aimPad) return undefined;

    const onTouchStart = (event) => {
      if (!isMyTurn || accuracyChallengeActive || !!animatedStones) {
        activeTouchIdRef.current = null;
        stopHandleDrag();
        return;
      }

      const touch = event.changedTouches?.[0];
      if (!touch) return;

      if (event.cancelable) {
        event.preventDefault();
      }

      const point = mapPointerToSheet(touch.clientX, touch.clientY, previewSvgRef.current);
      const targetHandle = event.target?.dataset?.handle || getNearestEditableHandle(point, editableHandles);
      activeTouchIdRef.current = touch.identifier;
      beginHandleDrag(targetHandle || 'power', point);
    };

    const onTouchMove = (event) => {
      if (!draggingRef.current || !activeHandleRef.current) return;

      const touchId = activeTouchIdRef.current;
      if (touchId === null || touchId === undefined) return;

      const touch = Array.from(event.touches || []).find((candidate) => candidate.identifier === touchId);
      if (!touch) return;

      if (event.cancelable) {
        event.preventDefault();
      }

      updateHandleFromPointer(activeHandleRef.current, mapPointerToSheet(touch.clientX, touch.clientY, previewSvgRef.current));
    };

    const onTouchEndOrCancel = (event) => {
      const touchId = activeTouchIdRef.current;
      if (touchId === null || touchId === undefined) return;

      const isStillActive = Array.from(event.touches || []).some((touch) => touch.identifier === touchId);
      if (isStillActive) return;

      if (event.cancelable) {
        event.preventDefault();
      }

      activeTouchIdRef.current = null;
      stopHandleDrag();
    };

    aimPad.addEventListener('touchstart', onTouchStart, { passive: false });
    aimPad.addEventListener('touchmove', onTouchMove, { passive: false });
    aimPad.addEventListener('touchend', onTouchEndOrCancel, { passive: false });
    aimPad.addEventListener('touchcancel', onTouchEndOrCancel, { passive: false });

    return () => {
      aimPad.removeEventListener('touchstart', onTouchStart);
      aimPad.removeEventListener('touchmove', onTouchMove);
      aimPad.removeEventListener('touchend', onTouchEndOrCancel);
      aimPad.removeEventListener('touchcancel', onTouchEndOrCancel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accuracyChallengeActive, animatedStones, editableHandles, isMyTurn]);

  useEffect(() => {
    return () => {
      if (endPauseTimeoutRef.current) {
        window.clearTimeout(endPauseTimeoutRef.current);
        endPauseTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 text-slate-900 dark:text-slate-100">
      <div className="flex flex-col gap-4">
        <div className="rounded-[2rem] border border-sky-200 bg-gradient-to-b from-sky-100 via-white to-sky-50 p-2.5 shadow-sm dark:border-sky-900/70 dark:from-sky-950/60 dark:via-slate-950 dark:to-sky-950/30 sm:p-3">
          <div className="mx-auto w-full max-w-[min(88vw,38rem)]">
            <div className="relative">
              <div
                ref={aimPadRef}
                onPointerDown={handleAimPointerDown}
                onPointerMove={handleAimPointerMove}
                onPointerUp={handleAimPointerUp}
                onPointerLeave={handleAimPointerLeave}
                onPointerCancel={handleAimPointerUp}
                onContextMenu={(event) => event.preventDefault()}
                onSelectStart={(event) => event.preventDefault()}
                className={`relative overflow-hidden rounded-[1.45rem] border border-white/70 bg-gradient-to-b from-sky-50 via-cyan-50 to-sky-100 shadow-inner select-none ${isMyTurn ? 'touch-none' : ''} dark:border-slate-700 dark:from-sky-950 dark:via-slate-900 dark:to-sky-950/80`}
                style={{ WebkitUserSelect: 'none', userSelect: 'none', WebkitTouchCallout: 'none', WebkitTapHighlightColor: 'transparent', touchAction: 'none' }}
              >
              <svg ref={previewSvgRef} viewBox="0 -6 100 158" className="block w-full overflow-visible">
                <defs>
                  <linearGradient id="curlingIceFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgba(240,249,255,0.96)" />
                    <stop offset="42%" stopColor="rgba(224,242,254,0.92)" />
                    <stop offset="72%" stopColor="rgba(239,248,255,0.9)" />
                    <stop offset="100%" stopColor="rgba(214,236,248,0.94)" />
                  </linearGradient>
                  <linearGradient id="curlingIceSheen" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="rgba(255,255,255,0.34)" />
                    <stop offset="35%" stopColor="rgba(255,255,255,0.08)" />
                    <stop offset="100%" stopColor="rgba(186,230,253,0.18)" />
                  </linearGradient>
                </defs>
                <rect x="8" y="-4" width="84" height="154" rx="11" fill="url(#curlingIceFill)" stroke="rgba(14,165,233,0.45)" strokeWidth="0.5" />
                <rect x="10" y="-2" width="80" height="150" rx="10" fill="url(#curlingIceSheen)" opacity="0.8" />
                <line x1="8" x2="92" y1="56" y2="56" stroke="rgba(220,38,38,0.55)" strokeWidth="0.55" strokeDasharray="1.8 1.2" />
                <line x1="8" x2="92" y1="28" y2="28" stroke="rgba(220,38,38,0.65)" strokeWidth="0.7" />
                <line x1="50" x2="50" y1="-4" y2="150" stroke="rgba(14,165,233,0.28)" strokeWidth="0.5" strokeDasharray="1.5 1.4" />
                <circle cx="50" cy="28" r="18" fill="rgba(147,197,253,0.28)" stroke="rgba(59,130,246,0.38)" strokeWidth="0.7" />
                <circle cx="50" cy="28" r="12.5" fill="rgba(248,250,252,0.72)" stroke="rgba(226,232,240,0.95)" strokeWidth="0.65" />
                <circle cx="50" cy="28" r="8" fill="rgba(251,191,36,0.22)" stroke="rgba(245,158,11,0.55)" strokeWidth="0.65" />
                <circle cx="50" cy="28" r="2.1" fill="rgba(220,38,38,0.8)" />
                <line x1="46" x2="54" y1="144" y2="144" stroke="rgba(15,23,42,0.6)" strokeWidth="0.7" />
                <line x1="48" x2="52" y1="146.2" y2="146.2" stroke="rgba(15,23,42,0.45)" strokeWidth="0.55" />

                {!gameState.finished && isMyTurn && !animatedStones ? (
                  <>
                    <path
                      d={preview.path}
                      fill="none"
                      stroke={previewStroke}
                      strokeWidth="1"
                      strokeDasharray="2.2 2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <circle cx={preview.endX} cy={preview.endY} r="1.15" fill={isMyTurn ? 'rgba(3,105,161,0.9)' : 'rgba(100,116,139,0.55)'} />
                    <line
                      x1={sheet.hackX}
                      y1={sheet.hackY}
                      x2={editableHandles.power.x}
                      y2={editableHandles.power.y}
                      stroke={previewStroke}
                      strokeWidth="0.75"
                      strokeDasharray="1.5 1.5"
                      opacity="0.32"
                    />
                    <line
                      x1={editableHandles.curveBase.x}
                      y1={editableHandles.curveBase.y}
                      x2={editableHandles.curve.x}
                      y2={editableHandles.curve.y}
                      stroke="rgba(249,115,22,0.45)"
                      strokeWidth="0.7"
                    />
                    <line
                      x1={editableHandles.spinBase.x}
                      y1={editableHandles.spinBase.y}
                      x2={editableHandles.spin.x}
                      y2={editableHandles.spin.y}
                      stroke="rgba(34,197,94,0.45)"
                      strokeWidth="0.7"
                    />
                    <circle
                      cx={editableHandles.power.x}
                      cy={editableHandles.power.y}
                      r="8"
                      fill="transparent"
                      onPointerDown={startHandleDrag('power')}
                      data-handle="power"
                      style={{ cursor: isMyTurn ? 'grab' : 'default' }}
                    />
                    <circle
                      cx={editableHandles.power.x}
                      cy={editableHandles.power.y}
                      r="2.45"
                      fill="rgba(14,165,233,0.72)"
                      stroke="rgba(255,255,255,0.95)"
                      strokeWidth="0.55"
                      onPointerDown={startHandleDrag('power')}
                      data-handle="power"
                      style={{ cursor: isMyTurn ? 'grab' : 'default' }}
                    />
                    <circle
                      cx={editableHandles.curve.x}
                      cy={editableHandles.curve.y}
                      r="8.2"
                      fill="transparent"
                      onPointerDown={startHandleDrag('curve')}
                      data-handle="curve"
                      style={{ cursor: isMyTurn ? 'grab' : 'default' }}
                    />
                    <circle
                      cx={editableHandles.curve.x}
                      cy={editableHandles.curve.y}
                      r="2.65"
                      fill="rgba(249,115,22,0.72)"
                      stroke="rgba(255,255,255,0.95)"
                      strokeWidth="0.55"
                      onPointerDown={startHandleDrag('curve')}
                      data-handle="curve"
                      style={{ cursor: isMyTurn ? 'grab' : 'default' }}
                    />
                    <circle
                      cx={editableHandles.spin.x}
                      cy={editableHandles.spin.y}
                      r="7.9"
                      fill="transparent"
                      onPointerDown={startHandleDrag('spin')}
                      data-handle="spin"
                      style={{ cursor: isMyTurn ? 'grab' : 'default' }}
                    />
                    <circle
                      cx={editableHandles.spin.x}
                      cy={editableHandles.spin.y}
                      r="2.55"
                      fill="rgba(34,197,94,0.72)"
                      stroke="rgba(255,255,255,0.95)"
                      strokeWidth="0.55"
                      onPointerDown={startHandleDrag('spin')}
                      data-handle="spin"
                      style={{ cursor: isMyTurn ? 'grab' : 'default' }}
                    />
                  </>
                ) : null}

                {renderedStones.map((stone) => (
                  <Stone key={stone.id} stone={stone} selected={stone.playerId === scoringLeaderId} />
                ))}
              </svg>
              </div>
            </div>

            <div className="mt-2 rounded-[1.25rem] border border-white/70 bg-white/80 p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div>
                  {endPauseActive && endPauseSummary ? (
                    <div className="w-full rounded-2xl border border-amber-200 bg-amber-50/90 px-3 py-3 text-left shadow-sm dark:border-amber-500/30 dark:bg-amber-500/10">
                      <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-200">End {endPauseSummary.endNumber} Complete</div>
                      <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-50">
                        {endPauseSummary.scoringPlayerName
                          ? `${endPauseSummary.scoringPlayerName} won the end with ${endPauseSummary.points} stone${endPauseSummary.points === 1 ? '' : 's'}.`
                          : 'Blank end. Nobody counted.'}
                      </div>
                    </div>
                  ) : accuracyChallengeActive ? (
                    <button
                      type="button"
                      onClick={completeAccuracyChallenge}
                      className="w-full rounded-2xl border border-white/80 bg-white/92 px-3 py-2 shadow-md dark:border-slate-700 dark:bg-slate-900"
                    >
                      <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Tap in the middle</div>
                      <div className="relative mt-1 h-3 w-full rounded-full bg-slate-200 dark:bg-slate-700">
                        <div className="absolute left-1/2 top-[-2px] h-4 w-6 -translate-x-1/2 rounded-full bg-emerald-500/30" />
                        <div className="absolute left-1/2 top-[-3px] h-5 w-[2px] -translate-x-1/2 rounded-full bg-emerald-600" />
                        <div
                          className="absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full border border-white/90 bg-sky-500 shadow"
                          style={{ left: `calc(${accuracyIndicatorPosition * 100}% - 0.5rem)` }}
                        />
                      </div>
                    </button>
                  ) : !gameState.finished ? (
                    <button
                      type="button"
                      onClick={startAccuracyChallenge}
                      disabled={!isMyTurn || !!gameState.finished || !!animatedStones}
                      className="w-full rounded-full bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-sky-500 dark:hover:bg-sky-400"
                    >
                      Throw {formatOrdinal(nextShotNumber)} Stone
                    </button>
                  ) : null}
                </div>

                <div className="grid grid-cols-4 gap-2">
                  <div className="rounded-2xl border border-white/75 bg-white/72 px-2 py-2 shadow-sm dark:border-slate-600 dark:bg-slate-800 dark:shadow-black/30">
                    <div className="text-[9px] uppercase tracking-[0.16em] text-slate-400 dark:text-slate-300">Angle</div>
                    <div className="mt-0.5 text-sm font-bold text-slate-700 dark:text-slate-50 sm:text-base">{formatDelta(shotSettings.angle, ' deg')}</div>
                  </div>
                  <div className="rounded-2xl border border-white/75 bg-white/72 px-2 py-2 shadow-sm dark:border-slate-600 dark:bg-slate-800 dark:shadow-black/30">
                    <div className="text-[9px] uppercase tracking-[0.16em] text-slate-400 dark:text-slate-300">Weight</div>
                    <div className="mt-0.5 text-sm font-bold text-slate-700 dark:text-slate-50 sm:text-base">{Math.round(shotSettings.power * 100)}%</div>
                  </div>
                  <div className="rounded-2xl border border-white/75 bg-white/72 px-2 py-2 shadow-sm dark:border-slate-600 dark:bg-slate-800 dark:shadow-black/30">
                    <div className="text-[9px] uppercase tracking-[0.16em] text-slate-400 dark:text-slate-300">Spin</div>
                    <div className="mt-0.5 text-sm font-bold text-slate-700 dark:text-slate-50 sm:text-base">{formatDelta(shotSettings.spin * 100, '%')}</div>
                  </div>
                  <div className="rounded-2xl border border-white/75 bg-white/72 px-2 py-2 shadow-sm dark:border-slate-600 dark:bg-slate-800 dark:shadow-black/30">
                    <div className="text-[9px] uppercase tracking-[0.16em] text-slate-400 dark:text-slate-300">Curve</div>
                    <div className="mt-0.5 text-sm font-bold text-slate-700 dark:text-slate-50 sm:text-base">{Math.round(shotSettings.curve * 100)}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white/90 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900/90">
            <div className="text-xs font-semibold uppercase tracking-[0.28em] text-slate-500">Scoreboard</div>
            <div className="mt-3 space-y-3">
              {rankedPlayers.map((player, index) => {
                const playerMatchScore = matchScores[player.socketId] || 0;
                const roundWins = player.score || 0;
                const isCurrentScoringLeader = currentScoringState.leaderId === player.socketId && currentScoringState.count > 0;
                return (
                  <div key={player.socketId} className="flex items-center justify-between gap-3 rounded-2xl bg-slate-50 px-3 py-3 dark:bg-slate-800/70">
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full text-sm font-black text-white" style={{ backgroundColor: player.color || '#0ea5e9' }}>
                        {index + 1}
                      </div>
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-900 dark:text-slate-100">{player.username}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400">Room wins {roundWins}</div>
                        {isCurrentScoringLeader ? (
                          <div className="mt-1 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-700 dark:bg-emerald-200 dark:text-emerald-700">
                            Counting {currentScoringState.count}
                          </div>
                        ) : null}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-black text-slate-900 dark:text-slate-100">{playerMatchScore}</div>
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">match</div>
                    </div>
                  </div>
                );
              })}
            </div>
        </div>
      </div>
    </div>
  );
}

CurlingGame.Guide = function CurlingGuide() {
  return (
    <div className="space-y-3">
      <h3 className="text-lg font-bold">How to Play Curling</h3>
      <p>Players take turns throwing stones from the hack toward the house at the far end of the sheet.</p>
      <p>Use the aim pad to set your line and weight, then add spin with the handle slider to make the stone curl left or right.</p>
      <p>At the end of each end, the player with the closest stone to the button scores. They count every one of their stones that sits closer than the nearest opposing stone.</p>
      <p>The match ends when someone reaches the target score or a clear leader emerges after the scheduled ends.</p>
    </div>
  );
};